import Logger from 'js-logger';
/**
 * Implements Google Analytics logging as referenced here:
 * https://quasar.dev/quasar-cli/developing-cordova-apps/managing-google-analytics
 */

/**
 * Helpers provided for Google Analytics (https://quasar.dev/quasar-cli/developing-cordova-apps/managing-google-analytics)
 * These require a global dataLayer object to be defined in index.template.html.
 */

/**
 * Log a Google Analytics event
 * @param {String} category
 * @param {String} action
 * @param {String} label
 * @param {String} sessionId
 */

export var logEventAnalytics = function logEventAnalytics(category, action, label) {
  var sessionId = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  window.dataLayer.push({
    appEventCategory: category,
    appEventAction: action,
    appEventLabel: label,
    sessionId: sessionId
  });
  window.dataLayer.push({
    event: 'appEvent'
  });
};
/**
 * Log a Google Analytics page view
 * @param {String} path
 * @param {String} name
 * @param {String} sessionId
 */

export var logPageAnalytics = function logPageAnalytics(path, name) {
  var sessionId = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  window.dataLayer.push({
    screenPath: path,
    screenName: name,
    sessionId: sessionId
  });
  window.dataLayer.push({
    event: 'appScreenView'
  });
};
var installed = false; // We have to define this using function to gain access to `arguments`, and we need to use a
// function here to push to window.dataLayer to correctly push to window.dataLayer in
// `enableAnalytics`.

function gtag() {
  window.dataLayer.push(arguments);
}

export var enableAnalytics = function enableAnalytics(enable) {
  var trackingCode = process.env.GOOGLE_ANALYTICS_TRACKING_CODE;

  if (!trackingCode) {
    return;
  }

  Logger.debug('changing state of Google Analytics', enable); // On first attempt to enable analytics, actually install the gtag script. This used to be in
  // index.template.html - but the issue with that is that we operate an opt-out service, and if the
  // user doesn't want cookies used, the installation of gtag itself sends analytics. So we
  // shouldn't install it until the user's actually consented.
  // See https://developers.google.com/tag-platform/security/guides/consent?consentmode=basic#upgrade-consent-v2 for more details.

  if (enable && !installed) {
    Logger.debug('installing gtag'); // Prevent multiple installations.

    installed = true; // Load gtag.js script

    var gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = "https://www.googletagmanager.com/gtag/js?id=".concat(trackingCode);
    var firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(gtagScript, firstScript);
  } // Update consent on state change.


  var value = enable ? 'granted' : 'denied';
  gtag('consent', 'update', {
    ad_user_data: value,
    ad_personalization: value,
    ad_storage: value,
    analytics_storage: value
  });
};
export default (function (_ref) {
  var router = _ref.router;
  router.afterEach(function (to) {
    var _to$meta;

    // We shouldn't bother Google unless we have a configured tracking code.
    if (!process.env.GOOGLE_ANALYTICS_TRACKING_CODE) {
      return;
    }

    var name = (to === null || to === void 0 ? void 0 : (_to$meta = to.meta) === null || _to$meta === void 0 ? void 0 : _to$meta.title) || '';
    Logger.debug("Notifying Google Analytics of navigation to path ".concat(to.path, " with name ").concat(name));
    logPageAnalytics(to.path, name);
  });
}); //* Google Tag Manager events
// survey lifecycle

export var surveyStarted = function surveyStarted(data) {
  var companyName = data.companyName,
      surveyTypeId = data.surveyTypeId,
      surveyId = data.surveyId; // set the user properties with the company name the survey type id and the survey id

  gtag('set', 'user_properties', {
    company_name: companyName,
    survey_type_id: surveyTypeId,
    survey_id: surveyId
  });
  window.dataLayer.push({
    event: 'survey-started',
    company_name: companyName,
    survey_type_id: surveyTypeId,
    survey_id: surveyId
  });
};
export var surveySubmitted = function surveySubmitted(data) {
  var companyName = data.companyName,
      surveyTypeId = data.surveyTypeId,
      surveyId = data.surveyId,
      sectionsCompleted = data.sectionsCompleted,
      sectionsCompletedNames = data.sectionsCompletedNames,
      sectionsSkipped = data.sectionsSkipped,
      sectionsSkippedNames = data.sectionsSkippedNames,
      sectionsTotal = data.sectionsTotal,
      answeredCount = data.answeredCount,
      totalQuestionsCount = data.totalQuestionsCount,
      branchId = data.branchId;
  window.dataLayer.push({
    event: 'submit-survey',
    company_name: companyName,
    survey_type_id: surveyTypeId,
    survey_id: surveyId,
    sections_completed: sectionsCompleted,
    sections_completed_names: sectionsCompletedNames,
    sections_skipped: sectionsSkipped,
    sections_skipped_names: sectionsSkippedNames,
    sections_total: sectionsTotal,
    answered_count: answeredCount,
    total_questions_count: totalQuestionsCount,
    branch_id: branchId
  });
};
export var surveyLeft = function surveyLeft(data) {
  var companyName = data.companyName,
      surveyTypeId = data.surveyTypeId,
      surveyId = data.surveyId,
      branchId = data.branchId;
  window.dataLayer.push({
    event: 'leave-survey',
    company_name: companyName,
    survey_type_id: surveyTypeId,
    survey_id: surveyId,
    branch_id: branchId
  });
};
export var surveyCompleted = function surveyCompleted(data) {
  var companyName = data.companyName,
      surveyTypeId = data.surveyTypeId,
      surveyId = data.surveyId,
      branchId = data.branchId;
  window.dataLayer.push({
    event: 'complete-survey',
    company_name: companyName,
    survey_type_id: surveyTypeId,
    survey_id: surveyId,
    branch_id: branchId
  });
};
export var surveyShared = function surveyShared(data) {
  var companyName = data.companyName,
      surveyTypeId = data.surveyTypeId,
      surveyId = data.surveyId,
      shareType = data.shareType,
      branchId = data.branchId;
  window.dataLayer.push({
    event: 'share-survey',
    company_name: companyName,
    survey_type_id: surveyTypeId,
    survey_id: surveyId,
    share_type: shareType,
    branch_id: branchId
  });
}; // question lifecycle

export var questionsStarted = function questionsStarted(data) {
  var companyName = data.companyName,
      surveyTypeId = data.surveyTypeId,
      surveyId = data.surveyId,
      sectionsTotal = data.sectionsTotal,
      branchId = data.branchId;
  window.dataLayer.push({
    event: 'start-questions',
    company_name: companyName,
    survey_type_id: surveyTypeId,
    survey_id: surveyId,
    sections_total: sectionsTotal,
    branch_id: branchId
  });
};
export var answersUpdated = function answersUpdated(data) {
  var companyName = data.companyName,
      surveyTypeId = data.surveyTypeId,
      surveyId = data.surveyId,
      answeredCount = data.answeredCount,
      branchId = data.branchId;
  window.dataLayer.push({
    event: 'update-answers',
    company_name: companyName,
    survey_type_id: surveyTypeId,
    survey_id: surveyId,
    answered_count: answeredCount,
    branch_id: branchId
  });
};
export var skipSection = function skipSection(data) {
  var companyName = data.companyName,
      surveyTypeId = data.surveyTypeId,
      surveyId = data.surveyId,
      section = data.section,
      branchId = data.branchId;
  window.dataLayer.push({
    event: 'skip-section',
    company_name: companyName,
    survey_type_id: surveyTypeId,
    survey_id: surveyId,
    section_title: section.title,
    branch_id: branchId
  });
}; // recommendations

export var generateAiRecommendation = function generateAiRecommendation(data) {
  var companyName = data.companyName,
      surveyTypeId = data.surveyTypeId,
      surveyId = data.surveyId,
      success = data.success,
      branchId = data.branchId;
  window.dataLayer.push({
    event: 'generate-ai-recommendation',
    company_name: companyName,
    survey_type_id: surveyTypeId,
    survey_id: surveyId,
    success_generated: success,
    branch_id: branchId
  });
};
export var shareAiRecommendation = function shareAiRecommendation(data) {
  var companyName = data.companyName,
      surveyTypeId = data.surveyTypeId,
      surveyId = data.surveyId,
      branchId = data.branchId;
  window.dataLayer.push({
    event: 'share-ai-recommendation',
    survey_type_id: surveyTypeId,
    survey_id: surveyId,
    company_name: companyName,
    branch_id: branchId
  });
};